import React from "react";
import "./Concept.css";
import { motion } from "framer-motion";
import {
  FaShieldAlt,
  FaSyncAlt,
  FaStar,
  FaMedal,
  FaHandshake,
  FaGlobe,
  FaLock,
  FaRoute,
  FaUserTag,
  FaPiggyBank,
  FaBalanceScale,
  FaSlidersH,
  FaPercentage,
  FaHistory,
  FaBan,
  FaEye,
} from "react-icons/fa";
import { useLanguage } from "../../context/LanguageContext";

const Concept = () => {
  const { t } = useLanguage();

  const pillarIcons = [
    <FaShieldAlt key="shield" />,
    <FaSyncAlt key="sync" />,
    <FaStar key="star" />,
  ];

  const ticketIcons = [
    <FaMedal key="legend" />,
    <FaHandshake key="partner" />,
    <FaGlobe key="digital" />,
  ];

  const fraudIcons = [
    <FaLock key="lock" />,
    <FaRoute key="route" />,
    <FaUserTag key="usertag" />,
    <FaPiggyBank key="piggy" />,
    <FaBalanceScale key="balance" />,
  ];

  const resaleIcons = [
    <FaSlidersH key="slider" />,
    <FaPercentage key="percent" />,
    <FaHistory key="history" />,
    <FaBan key="ban" />,
    <FaEye key="eye" />,
  ];

  return (
    <section className="concept-section" id="concept">
      <div className="concept-container">
        {/* HEADER */}
        <div className="section-header">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            {t.concept.title}{" "}
            <span className="text-gold">{t.concept.titleSpan}</span>
          </motion.h2>
          <motion.div
            className="section-line"
            initial={{ width: 0 }}
            whileInView={{ width: "100px" }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          ></motion.div>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3, duration: 0.6 }}
          >
            {t.concept.subtitle}
          </motion.p>
        </div>

        {/* =========================================
            SÉQUENCE 1 : FAN ENGAGEMENT (Pilier à droite)
        ========================================= */}
        <div className="concept-layout">
          <div className="layout-left">
            <div className="pillars-top-row">
              {t.concept.pillars.slice(0, 2).map((pillar, index) => (
                <motion.div
                  key={index}
                  className="pillar-card base-border"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.2, duration: 0.5 }}
                >
                  <div className="pillar-icon">{pillarIcons[index]}</div>
                  <h3>{pillar.title}</h3>
                  <h4 className="text-gold">{pillar.subtitle}</h4>
                  <p>{pillar.desc}</p>
                </motion.div>
              ))}
            </div>

            <div className="tickets-wrapper connected-card-wrapper">
              <div className="tickets-list">
                {t.concept.tickets.map((ticket, index) => (
                  <motion.div
                    key={index}
                    className="ticket-detail-card"
                    initial={{ opacity: 0, x: -30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, margin: "-10% 0px 0px 0px" }}
                    transition={{ delay: index * 0.1, duration: 0.4 }}
                  >
                    <div className="ticket-icon">{ticketIcons[index]}</div>
                    <div className="ticket-content">
                      <h3>{ticket.title}</h3>
                      <div className="ticket-features">
                        {ticket.features.map((feature, idx) => (
                          <p key={idx}>
                            <strong className="text-gold">
                              {feature.label} :{" "}
                            </strong>
                            {feature.text}
                          </p>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>

          <div className="layout-right">
            <div className="sticky-wrapper">
              <motion.div
                className="pillar-card highlight-card connected-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <div className="pillar-icon highlight-icon">
                  {pillarIcons[2]}
                </div>
                <h3>{t.concept.pillars[2].title}</h3>
                <h4 className="text-gold">{t.concept.pillars[2].subtitle}</h4>
                <p>{t.concept.pillars[2].desc}</p>
              </motion.div>
            </div>
          </div>
        </div>
        <div className="weather-div">
          <p>{t.concept.weather}</p>
        </div>

        {/* Punchline 1 */}
        <motion.div
          className="concept-conclusion"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <h3>{t.concept.conclusionFan}</h3>
        </motion.div>

        {/* =========================================
            SÉQUENCE 2 : ANTI-FRAUDE (Pilier à gauche)
        ========================================= */}
        <div
          className="concept-layout layout-reverse"
          style={{ marginTop: "150px" }}
        >
          <div className="layout-left-sticky">
            <div className="sticky-wrapper">
              <motion.div
                className="pillar-card highlight-card connected-card antifraud-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <div className="pillar-icon highlight-icon">
                  {pillarIcons[0]}
                </div>
                <h3>{t.concept.pillars[0].title}</h3>
                <h4 className="text-gold">{t.concept.pillars[0].subtitle}</h4>
                <p>{t.concept.pillars[0].desc}</p>
              </motion.div>
            </div>
          </div>

          <div className="layout-right-scroll">
            <div className="tickets-wrapper connected-card-wrapper">
              <div className="tickets-list">
                {t.concept.antifraudFeatures.map((feature, index) => (
                  <motion.div
                    key={index}
                    className="ticket-detail-card small-card"
                    initial={{ opacity: 0, x: 30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, margin: "-10% 0px 0px 0px" }}
                    transition={{ delay: index * 0.1, duration: 0.4 }}
                  >
                    <div className="ticket-icon">{fraudIcons[index]}</div>
                    <div className="ticket-content">
                      <h3 style={{ marginBottom: "10px" }}>{feature.title}</h3>
                      <p style={{ color: "#ccc", lineHeight: "1.5" }}>
                        {feature.text}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Punchline 2 */}
        <motion.div
          className="concept-conclusion"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <h3>{t.concept.conclusionFraud}</h3>
        </motion.div>

        {/* =========================================
            SÉQUENCE 3 : REVENTE (Pilier Central)
        ========================================= */}
        <div className="seq3-centered-wrapper" style={{ marginTop: "150px" }}>
          {/* Pilier central Sticky */}
          <div className="seq3-sticky-header">
            <motion.div
              className="pillar-card highlight-card connected-card center-card"
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="pillar-icon highlight-icon">{pillarIcons[1]}</div>
              <div className="center-card-text">
                <h2>{t.concept.pillars[1].title}</h2>
                <h4 className="text-gold">{t.concept.pillars[1].subtitle}</h4>
                <p>{t.concept.pillars[1].desc}</p>
              </div>
            </motion.div>
          </div>

          {/* Grille de cartes qui défile en dessous */}
          <div className="seq3-cards-grid">
            {t.concept.resaleFeatures.map((feature, index) => (
              <motion.div
                key={index}
                className="zoomed-feature-card"
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-5% 0px 0px 0px" }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
              >
                <div className="ticket-icon">{resaleIcons[index]}</div>
                <h3>{feature.title}</h3>
                <p>{feature.text}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Punchline 3 */}
        <motion.div
          className="concept-conclusion"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <h3>{t.concept.conclusionResale}</h3>
        </motion.div>
      </div>
    </section>
  );
};

export default Concept;
